import type { Agency } from '@saaga/shared/interfaces/agencies.interface'
import type { ReactNode } from 'react'

import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

import { AgencyCard } from './agency-card.molecule'

interface AgencyGridProps {
  agencyList: Agency[]
  pinnedAgencyIdList?: string[]
  isLoading?: boolean
  isVisibilityBadgeVisible?: boolean
  isAgencyEditable?: (agency: Agency) => boolean
  isAgencyDeletable?: (agency: Agency) => boolean
  isStartChatVisible?: (agency: Agency) => boolean
  emptyState?: ReactNode
  onStartChat?: (agency: Agency) => void
  onSeeDetails?: (agency: Agency) => void
  onEdit?: (agency: Agency) => void
  onDelete?: (agency: Agency) => void
  onAgencyClick?: (agency: Agency) => void
  onPin?: (agency: Agency) => void
  className?: string
}

const SkeletonCard = (): ReactNode => (
  <div className="agency-card-skeleton rounded-xl border bg-card p-6 space-y-4">
    <div className="flex items-start gap-3">
      <Skeleton className="size-12 rounded-lg" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-5 w-24" />
      </div>
    </div>
    <div className="space-y-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
    </div>
    <div className="flex gap-2">
      <Skeleton className="h-5 w-16" />
      <Skeleton className="h-5 w-20" />
    </div>
    <div className="flex gap-4">
      <Skeleton className="h-4 w-20" />
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-4 w-16" />
    </div>
    <div className="flex gap-2">
      <Skeleton className="h-9 w-28" />
      <Skeleton className="h-9 w-24" />
    </div>
  </div>
)

const SKELETON_KEY_LIST = ['sk-1', 'sk-2', 'sk-3', 'sk-4', 'sk-5', 'sk-6']

const LoadingSkeleton = (): ReactNode => (
  <div className="agency-grid grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
    {SKELETON_KEY_LIST.map((key) => (
      <SkeletonCard key={key} />
    ))}
  </div>
)

const defaultIsEditable = (): boolean => true
const defaultIsVisible = (): boolean => true

export const AgencyGrid = ({
  agencyList,
  pinnedAgencyIdList = [],
  isLoading = false,
  isVisibilityBadgeVisible = false,
  isAgencyEditable = defaultIsEditable,
  isAgencyDeletable = defaultIsEditable,
  isStartChatVisible = defaultIsVisible,
  emptyState,
  onStartChat,
  onSeeDetails,
  onEdit,
  onDelete,
  onAgencyClick,
  onPin,
  className,
}: AgencyGridProps): ReactNode => {
  if (isLoading) {
    return <LoadingSkeleton />
  }

  if (agencyList.length === 0 && emptyState) {
    return <>{emptyState}</>
  }

  return (
    <div
      className={cn(
        'agency-grid grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4',
        className
      )}
    >
      {agencyList.map((agency) => (
        <AgencyCard
          key={agency.id}
          agency={agency}
          isPinned={pinnedAgencyIdList.includes(agency.id)}
          isVisibilityBadgeVisible={isVisibilityBadgeVisible}
          onStartChat={onStartChat && isStartChatVisible(agency) ? onStartChat : undefined}
          onSeeDetails={onSeeDetails}
          onEdit={onEdit && isAgencyEditable(agency) ? onEdit : undefined}
          onDelete={onDelete && isAgencyDeletable(agency) ? onDelete : undefined}
          onCardClick={onAgencyClick}
          onPin={onPin}
        />
      ))}
    </div>
  )
}
